import api from './axios';
import { logError, logInfo } from '../common/utils/logger';
import { API_ENDPOINTS, PRODUCT_CODES_NISCHIT_PENSION } from '../constants';
import { encryptJourneyQueryValue } from '../common/utils/journeyEncryption';
import { getSafeRedirectUrl } from '../common/utils/safeNavigation';

const RESUME_DOB_SESSION_KEY = 'rnlic_resumeDob';
const getResumeDobStorageKey = (applicationNumber) =>
  `${RESUME_DOB_SESSION_KEY}:${String(applicationNumber || "").trim()}`;

const readStoredResumeDob = (applicationNumber) => {
  try {
    const dob =
      (applicationNumber
        ? sessionStorage.getItem(getResumeDobStorageKey(applicationNumber))
        : null) ||
      sessionStorage.getItem(RESUME_DOB_SESSION_KEY);
    return typeof dob === 'string' && dob.trim() ? dob.trim() : null;
  } catch {
    return null;
  }
};

const readDobFromLeadFormSession = () => {
  try {
    const rawLeadForm = sessionStorage.getItem("inlic_lead_form_data");
    if (!rawLeadForm) return null;
    const parsedLeadForm = JSON.parse(rawLeadForm);
    const dob = parsedLeadForm?.dateOfBirth;
    return typeof dob === "string" && dob.trim() ? dob.trim() : null;
  } catch {
    return null;
  }
};

const resolveOwnershipDob = (applicationNumber, explicitDob) => {
  const explicit =
    typeof explicitDob === "string" && explicitDob.trim()
      ? explicitDob.trim()
      : null;
  if (explicit) return explicit;
  return readStoredResumeDob(applicationNumber) || readDobFromLeadFormSession();
};

/**
 * Proof of payment initiation for returnContext when HTTP session is missing (post-gateway).
 * Uses sessionStorage to avoid cross-session sensitive persistence.
 */
const PAYMENT_RETURN_PROOF_LOCAL_PREFIX = 'rnlic_paymentReturnProof:v1:';
const PAYMENT_RETURN_PROOF_TTL_MS = 2 * 60 * 60 * 1000;

const paymentReturnProofKey = (applicationNumber) =>
  `${PAYMENT_RETURN_PROOF_LOCAL_PREFIX}${String(applicationNumber || '').trim()}`;

const storePaymentReturnProof = (applicationNumber, proof) => {
  try {
    if (!applicationNumber || !proof || typeof proof !== 'object') return;
    const app = String(applicationNumber).trim();
    if (!app) return;
    sessionStorage.setItem(
      paymentReturnProofKey(app),
      JSON.stringify({
        transactionId: proof.transactionId != null ? String(proof.transactionId).trim() : '',
        redirectionKey: proof.redirectionKey != null ? String(proof.redirectionKey).trim() : '',
        orderId: proof.orderId != null ? String(proof.orderId).trim() : '',
        dateOfBirth: proof.dateOfBirth != null ? String(proof.dateOfBirth).trim() : '',
        exp: Date.now() + PAYMENT_RETURN_PROOF_TTL_MS,
      }),
    );
  } catch {
    /* ignore */
  }
};

const readStoredPaymentReturnProof = (applicationNumber) => {
  try {
    const app = String(applicationNumber || '').trim();
    if (!app) return null;
    const raw = sessionStorage.getItem(paymentReturnProofKey(app));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.exp != null && Date.now() > Number(parsed.exp)) {
      sessionStorage.removeItem(paymentReturnProofKey(app));
      return null;
    }
    return {
      transactionId: parsed.transactionId,
      redirectionKey: parsed.redirectionKey,
      orderId: parsed.orderId,
      dateOfBirth: parsed.dateOfBirth,
    };
  } catch {
    return null;
  }
};

const clearPaymentReturnProof = (applicationNumber) => {
  try {
    const app = String(applicationNumber || '').trim();
    if (!app) return;
    sessionStorage.removeItem(paymentReturnProofKey(app));
  } catch {
    /* ignore */
  }
};


/** BI API product IDs for quote submit and payment (must be sent as productId / prId, not bisOpsProductCode). */
export const BI_API_PRODUCT_ID_ELITE = PRODUCT_CODES_NISCHIT_PENSION.PLAN_B;
export const BI_API_PRODUCT_ID_PLUS = PRODUCT_CODES_NISCHIT_PENSION.PLAN_A;

/**
 * Resolves plan name or product code to BI API product ID (22070 Pension, 22074 Pro).
 * @param {string} planNameOrCode
 * @returns {string|null} "22070", "22074", or null
 */
export const resolveBiApiProductId = (planNameOrCode) => {
  if (planNameOrCode == null || String(planNameOrCode).trim() === '') return null;
  const s = String(planNameOrCode).trim().toUpperCase();
  if (s === '22070' || s === '22074') return s;
  if (s.includes('PRO')) return PRODUCT_CODES_NISCHIT_PENSION.PLAN_A;
  if (s.includes('PENSION') || s.includes('NISCHIT')) {
    return PRODUCT_CODES_NISCHIT_PENSION.PLAN_B;
  }
  return null;
};

const hasNonEmptyValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== '';

const validatePaymentData = (paymentData) => {
  if (!paymentData || typeof paymentData !== 'object') {
    throw new Error('Payment data is required.');
  }

  const amount = paymentData.amount ?? paymentData.transactionAmount;
  const productId = paymentData.productId ?? paymentData.transactionProductId;
  const hasProductId = hasNonEmptyValue(productId);
  const hasBisOpsCode = hasNonEmptyValue(paymentData.bisOpsProductCode);

  const requiredChecks = [
    [amount, 'Payment amount is required (amount or transactionAmount).'],
    [paymentData.customerName, 'Customer name is required.'],
    [paymentData.applicationNumber, 'Application number is required.'],
    [paymentData.mobileNumber, 'Mobile number is required.'],
    [paymentData.emailAddress, 'Email address is required.'],
  ];

  for (const [value, message] of requiredChecks) {
    if (!value) throw new Error(message);
  }

  if (!hasProductId && !hasBisOpsCode) {
    throw new Error('Product ID (or transactionProductId) or bisOpsProductCode is required.');
  }
};

const encryptPaymentPayloadFields = (paymentData) => {
  const payload = { ...paymentData };
  const plainApp = String(payload.applicationNumber ?? '').trim();
  if (plainApp) {
    payload.applicationNumber = encryptJourneyQueryValue(plainApp);
  }
  for (const field of ['dateOfBirth', 'dob']) {
    if (hasNonEmptyValue(payload[field])) {
      payload[field] = encryptJourneyQueryValue(String(payload[field]).trim());
    }
  }
  return payload;
};

const parsePaymentUrlResponse = (rawData) => {
  let data = rawData;
  if (data != null && typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) throw new Error('Empty response from payment service.');
    try {
      data = JSON.parse(trimmed);
    } catch {
      throw new Error('Invalid response from payment service.');
    }
  }
  if (!data || typeof data !== 'object') {
    throw new Error('Empty response from payment service.');
  }
  return data;
};

const buildPaymentUrlSuccessResult = (data) => ({
  success: true,
  flow: data.flow || 'GATEWAY_REDIRECT',
  paymentUrl: data.paymentUrl,
  transactionId: data.transactionId,
  orderId: data.orderId,
  redirectionKey: data.redirectionKey,
});

export const PAYMENT_INITIATION_FLOW = {
  ALREADY_PAID: 'ALREADY_PAID',
  GATEWAY_REDIRECT: 'GATEWAY_REDIRECT',
  PAYMENT_AWAITING: 'PAYMENT_AWAITING',
};

const buildInitiatePaymentResult = (data) => {
  if (data.flow === PAYMENT_INITIATION_FLOW.ALREADY_PAID) {
    return {
      success: true,
      alreadyPaid: true,
      flow: PAYMENT_INITIATION_FLOW.ALREADY_PAID,
      transactionStatus: data.transactionStatus || 'SUCCESS',
      transactionId: data.transactionId,
      transactionAmount: data.transactionAmount,
      redirectionKey: data.redirectionKey,
      applicationNumber: data.applicationNumber,
      message: data.message,
    };
  }
  if (data.flow === PAYMENT_INITIATION_FLOW.PAYMENT_AWAITING) {
    return {
      success: true,
      paymentAwaiting: true,
      flow: PAYMENT_INITIATION_FLOW.PAYMENT_AWAITING,
      transactionStatus: data.transactionStatus || 'AWAITING_RESPONSE',
      applicationNumber: data.applicationNumber,
      message:
        data.message ||
        "We're verifying your payment. If this takes a bit longer, you can still continue via 'Retrieve Application'",
    };
  }
  return buildPaymentUrlSuccessResult(data);
};

/**
 * Initiates payment via orchestration API (status check first, then gateway URL if needed).
 */
export const initiatePaymentFlow = async (paymentData) => {
  try {
    validatePaymentData(paymentData);
    logInfo('[initiatePaymentFlow] Initiating payment for application:', paymentData.applicationNumber);
    const payload = encryptPaymentPayloadFields(paymentData);
    const response = await api.post(API_ENDPOINTS.PAYMENT_INITIATE, payload);
    const data = parsePaymentUrlResponse(response?.data);
    if (data.success === false) {
      logError('[initiatePaymentFlow] Payment initiation failed:', data.errorMessage);
      return {
        success: false,
        errorMessage: data.errorMessage || 'Failed to initiate payment.',
      };
    }
    return buildInitiatePaymentResult(data);
  } catch (error) {
    logError('[initiatePaymentFlow] Failed to initiate payment', error);
    return {
      success: false,
      errorMessage: error.message || 'An error occurred while initiating payment.',
    };
  }
};

const validateJourneyPaymentFields = ({
  applicationNumber,
  customerName,
  premiumAmount,
  mobileNumber,
  emailAddress,
}) => {
  if (!applicationNumber) throw new Error('Application number is required.');
  if (!customerName) throw new Error('Customer name is required.');
  if (premiumAmount === undefined || premiumAmount === null) {
    throw new Error('Premium amount is required.');
  }
  if (!mobileNumber) throw new Error('Mobile number is required.');
  if (!emailAddress) throw new Error('Email address is required.');
};

const resolvePaymentProductId = (productId, productCode, bisOpsProductCode) => {
  const rawId = productId !== undefined && productId !== null ? String(productId).trim() : '';
  const isAlreadyBiApiId =
    rawId === BI_API_PRODUCT_ID_ELITE || rawId === BI_API_PRODUCT_ID_PLUS;
  return isAlreadyBiApiId
    ? rawId
    : resolveBiApiProductId(productId || productCode || bisOpsProductCode || '');
};

const normalizeReceiptNumber = (receiptNumber) => {
  if (receiptNumber == null || String(receiptNumber).trim() === '') return null;
  return Number(String(receiptNumber).trim());
};

const resolvePaymentReturnFields = (params) => {
  const {
    applicationNumber,
    applicationNo,
    dateOfBirth,
    dob,
    transactionId,
    transactionNo,
    redirectionKey,
    orderId,
  } = params || {};

  const resolvedApplicationNumber = String(applicationNumber || applicationNo || '').trim();
  if (!resolvedApplicationNumber) {
    throw new Error('Application number is required for payment return context.');
  }

  const proof = readStoredPaymentReturnProof(resolvedApplicationNumber);

  return {
    resolvedApplicationNumber,
    applicationNo,
    resolvedDob: String(
      resolveOwnershipDob(resolvedApplicationNumber, dateOfBirth || dob) ||
        proof?.dateOfBirth ||
        '',
    ).trim(),
    resolvedTxn: String(transactionId || transactionNo || proof?.transactionId || '').trim(),
    resolvedKey: String(redirectionKey || proof?.redirectionKey || '').trim(),
    resolvedOrder: String(orderId || proof?.orderId || '').trim(),
  };
};

const optionalTrimmedString = (value) =>
  value != null && value !== '' ? String(value).trim() : null;

const buildPaymentReturnContextPayload = (params, resolved) => {
  const { productId, status, amount } = params || {};
  const {
    resolvedApplicationNumber,
    applicationNo,
    resolvedDob,
    resolvedTxn,
    resolvedKey,
    resolvedOrder,
  } = resolved;

  const payload = {
    applicationNumber: encryptJourneyQueryValue(resolvedApplicationNumber),
  };

  const optionalFields = [
    [applicationNo, 'applicationNo', (v) => encryptJourneyQueryValue(v)],
    [productId, 'productId', (v) => v],
    [status, 'status', (v) => v],
    [amount, 'amount', (v) => v],
    [resolvedDob, 'dateOfBirth', (v) => encryptJourneyQueryValue(v)],
    [resolvedTxn, 'transactionId', (v) => v],
    [resolvedKey, 'redirectionKey', (v) => v],
    [resolvedOrder, 'orderId', (v) => v],
  ];

  for (const [value, key, transform] of optionalFields) {
    const trimmed = optionalTrimmedString(value);
    if (trimmed) payload[key] = transform(trimmed);
  }

  if (typeof window !== 'undefined') {
    if (window.location?.origin) payload.returnOrigin = window.location.origin;
    if (window.location?.pathname) payload.returnPath = window.location.pathname;
  }

  return payload;
};

const extractServerErrorMessage = (error) => {
  const ax = error?.response?.data;
  if (ax == null) return null;

  if (typeof ax === 'string') {
    try {
      const parsed = JSON.parse(ax);
      return parsed?.errorMessage || parsed?.error || ax;
    } catch {
      return ax;
    }
  }

  if (typeof ax === 'object') {
    return ax.errorMessage || ax.error;
  }

  return null;
};

/**
 * Generate payment gateway URL for online payments
 * 
 * This function calls the backend API to generate a payment gateway redirect URL.
 * Supports both legacy fields and updated API payload.
 * 
 * Updated API payload fields:
 * - applicationNumber, receiptNumber, transactionAmount, transactionDate, transactionId,
 *   transactionInitiatedStage, transactionProductId, transactionRedirectionKey,
 *   transactionRedirectionURL, transactionStatus
 * 
 * Legacy fields (still used for SOAP): amount, customerName, mobileNumber, emailAddress, productId, paymentDate
 * 
 * @param {Object} paymentData - Payment request data
 * @param {string} paymentData.applicationNumber - Journey application number (required)
 * @param {string} [paymentData.amount] - Payment amount (or use transactionAmount)
 * @param {string} [paymentData.transactionAmount] - Transaction amount (or use amount)
 * @param {string} paymentData.customerName - Customer's full name (required)
 * @param {string} paymentData.mobileNumber - Customer mobile number (required)
 * @param {string} paymentData.emailAddress - Customer email address (required)
 * @param {string|number} [paymentData.productId] - Product ID (or use transactionProductId)
 * @param {number} [paymentData.transactionProductId] - Transaction product ID (or use productId)
 * @param {number} [paymentData.receiptNumber] - Receipt number
 * @param {string} [paymentData.transactionDate] - Transaction date (yyyy-MM-dd or DD-MM-YYYY)
 * @param {string} [paymentData.transactionInitiatedStage] - Stage when transaction was initiated
 * @param {string} [paymentData.transactionId] - Transaction ID (auto-generated if not provided)
 * @param {string} [paymentData.transactionRedirectionKey] - Redirection key from gateway
 * @param {string} [paymentData.transactionRedirectionURL] - Redirection URL
 * @param {string} [paymentData.transactionStatus] - Transaction status (e.g. INITIATED)
 * @returns {Promise<Object>} Response object with success status and paymentUrl or errorMessage
 */
export const generatePaymentUrl = async (paymentData) => {
  try {
    const result = await initiatePaymentFlow(paymentData);
    if (!result.success) {
      return result;
    }
    if (result.alreadyPaid) {
      return result;
    }
    logInfo('[generatePaymentUrl] Payment URL generated successfully');
    return result;
  } catch (error) {
    logError('[generatePaymentUrl] Failed to generate payment URL', error);
    return {
      success: false,
      errorMessage: error.message || 'An error occurred while generating the payment URL.',
    };
  }
};

/**
 * Initiate payment flow by generating URL and redirecting to payment gateway
 * 
 * This is a convenience function that generates the payment URL and handles
 * the redirect to the payment gateway.
 * 
 * @param {Object} paymentData - Payment request data (same as generatePaymentUrl)
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.openInNewTab=false] - Open payment page in new tab instead of redirect
 * @returns {Promise<Object>} Response object with success status
 */
export const initiatePayment = async (paymentData, options = {}) => {
  try {
    const result = await initiatePaymentFlow(paymentData);

    if (!result.success) {
      return result;
    }

    if (result.alreadyPaid || result.flow === PAYMENT_INITIATION_FLOW.ALREADY_PAID) {
      logInfo('[initiatePayment] Payment already completed for application:', paymentData.applicationNumber);
      return {
        success: true,
        alreadyPaid: true,
        flow: PAYMENT_INITIATION_FLOW.ALREADY_PAID,
        transactionStatus: result.transactionStatus || 'SUCCESS',
        transactionId: result.transactionId,
      };
    }

    if (result.paymentAwaiting || result.flow === PAYMENT_INITIATION_FLOW.PAYMENT_AWAITING) {
      logInfo('[initiatePayment] Payment awaiting gateway response for application:', paymentData.applicationNumber);
      return {
        success: true,
        paymentAwaiting: true,
        flow: PAYMENT_INITIATION_FLOW.PAYMENT_AWAITING,
        transactionStatus: result.transactionStatus || 'AWAITING_RESPONSE',
        message:
          result.message ||
          "We're verifying your payment. If this takes a bit longer, you can still continue via 'Retrieve Application'",
      };
    }

    const { paymentUrl } = result;

    if (!paymentUrl) {
      return {
        success: false,
        errorMessage: 'Payment URL is empty.'
      };
    }

    logInfo('[initiatePayment] Redirecting to payment gateway');

    storePaymentReturnProof(paymentData.applicationNumber, {
      transactionId: result.transactionId,
      orderId: result.orderId,
      redirectionKey: result.redirectionKey,
      dateOfBirth:
        resolveOwnershipDob(
          paymentData.applicationNumber,
          paymentData?.dateOfBirth || paymentData?.dob || paymentData?.liDob,
        ) || "",
    });

    // Handle redirect based on options
    const safePaymentUrl = getSafeRedirectUrl(paymentUrl);
    if (!safePaymentUrl) {
      return {
        success: false,
        errorMessage: 'Blocked unsafe payment redirect URL.',
      };
    }

    if (options.openInNewTab) {
      window.open(safePaymentUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = safePaymentUrl;
    }

    return {
      success: true,
      paymentUrl,
      redirected: true
    };
  } catch (error) {
    logError('[initiatePayment] Failed to initiate payment', error);
    return {
      success: false,
      errorMessage: error.message || 'An error occurred while initiating payment.'
    };
  }
};

/**
 * Format date as yyyy-MM-dd for updated API payload.
 */
const formatTransactionDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Build payment request from journey data (updated API payload + legacy fields for SOAP).
 * 
 * Returns an object matching the updated payment API payload where applicable:
 * applicationNumber, receiptNumber, transactionAmount, transactionDate, transactionId,
 * transactionInitiatedStage, transactionProductId, transactionRedirectionKey,
 * transactionRedirectionURL, transactionStatus — plus customerName, mobileNumber,
 * emailAddress for gateway SOAP call.
 * 
 * @param {Object} journeyData - Journey/quote data from the summary screen
 * @param {string} journeyData.applicationNumber - Application number (required)
 * @param {string} journeyData.customerName - Customer's full name
 * @param {number|string} journeyData.premiumAmount - Premium amount to pay
 * @param {string} journeyData.mobileNumber - Customer mobile number
 * @param {string} journeyData.emailAddress - Customer email address
 * @param {string|number} [journeyData.productId] - Product ID for payment gateway
 * @param {string} [journeyData.productCode] - Product code (resolved via PaymentRedirectionMaster if productId missing)
 * @param {string} [journeyData.bisOpsProductCode] - BIS Ops product code (e.g. term22062) for resolution
 * @param {number} [journeyData.receiptNumber] - Receipt number
 * @param {string} [journeyData.currentStage] - Current journey stage (for transactionInitiatedStage)
 * @param {string} [journeyData.paymentDate] - Payment date (DD-MM-YYYY)
 * @returns {Object} Payment request data ready for generatePaymentUrl
 */
export const buildPaymentRequest = (journeyData) => {
  if (!journeyData) {
    throw new Error('Journey data is required to build payment request.');
  }

  const {
    applicationNumber,
    customerName,
    premiumAmount,
    mobileNumber,
    emailAddress,
    productId,
    productCode,
    bisOpsProductCode,
    paymentDate,
    receiptNumber,
    currentStage,
  } = journeyData;

  validateJourneyPaymentFields({
    applicationNumber,
    customerName,
    premiumAmount,
    mobileNumber,
    emailAddress,
  });

  const resolvedProductId = resolvePaymentProductId(productId, productCode, bisOpsProductCode);
  if (!resolvedProductId) {
    throw new Error('Product ID (22070 or 22074 for Nishchit Pension) or plan/product code is required for payment.');
  }

  const transactionAmount = String(premiumAmount);
  const transactionDate = paymentDate || formatTransactionDate();
  const normalizedReceiptNumber = normalizeReceiptNumber(receiptNumber);
  const receiptPayload =
    Number.isFinite(normalizedReceiptNumber) && normalizedReceiptNumber > 0
      ? { receiptNumber: normalizedReceiptNumber }
      : {};

  return {
    applicationNumber,
    ...receiptPayload,
    amount: transactionAmount,
    transactionDate,
    transactionInitiatedStage: currentStage ?? '',
    transactionStatus: 'INITIATED',
    customerName,
    mobileNumber,
    emailAddress,
    productId: resolvedProductId,
    paymentDate: paymentDate || undefined,
  };
};

/**
 * Get payment return context when user lands back from payment gateway.
 * Returns productJourneyPage, transactionInitiatedStage, etc. for showing popup and redirecting.
 *
 * @param {Object} params - Callback params from payment return URL
 * @param {string} params.applicationNumber - Application number (required)
 * @param {string} [params.productId] - Product ID for resolving journey page
 * @param {string} [params.status] - SUCCESS or FAILED
 * @param {string} [params.amount] - Payment amount
 * @param {string} [params.dateOfBirth] - Plain DOB when included in POST body (returnContext).
 * @returns {Promise<Object>} { productJourneyPage, applicationNumber, status, amount, transactionInitiatedStage }
 */
export const getPaymentReturnContext = async (params) => {
  try {
    const resolved = resolvePaymentReturnFields(params);
    const payload = buildPaymentReturnContextPayload(params, resolved);
    const response = await api.post(API_ENDPOINTS.PAYMENT_RETURN_CONTEXT, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    const data = typeof response?.data === 'string' ? JSON.parse(response.data) : response?.data;
    clearPaymentReturnProof(resolved.resolvedApplicationNumber);
    return data || {};
  } catch (error) {
    logError('[getPaymentReturnContext] Failed to get payment return context', error);
    const serverMsg = extractServerErrorMessage(error);
    if (serverMsg) {
      throw new Error(String(serverMsg));
    }
    throw error;
  }
};

/**
 * Downloads the Premium Deposit Receipt PDF for a successful payment.
 * Uses applicationNumber and transactionId (from payment return context).
 *
 * @param {Object} params
 * @param {string} params.applicationNumber - Application number (required)
 * @param {string} params.transactionId - Transaction ID from payment return context (required)
 * @returns {Promise<void>} Resolves when download is triggered; rejects on error
 */
export const downloadReceipt = async (params) => {
  const { applicationNumber, transactionId } = params || {};
  if (!applicationNumber || String(applicationNumber).trim() === '') {
    throw new Error('Application number is required for receipt download.');
  }
  if (!transactionId || String(transactionId).trim() === '') {
    throw new Error('Transaction ID is required for receipt download.');
  }
  const proof = readStoredPaymentReturnProof(applicationNumber);
  const dateOfBirth = String(
    resolveOwnershipDob(applicationNumber) || proof?.dateOfBirth || '',
  ).trim();
  const search = new URLSearchParams({
    applicationNumber: encryptJourneyQueryValue(applicationNumber),
    transactionId: encryptJourneyQueryValue(transactionId),
    ...(dateOfBirth ? { dateOfBirth: encryptJourneyQueryValue(dateOfBirth) } : {}),
  }).toString();
  const response = await api.get(`${API_ENDPOINTS.PAYMENT_RECEIPT}?${search}`, {
    responseType: 'blob',
  });
  const blob = response?.data;
  if (!blob || !(blob instanceof Blob)) {
    throw new Error('Invalid receipt response.');
  }
  const disposition = response.headers?.['content-disposition'];
  let filename = `PremiumDepositReceipt_${String(applicationNumber).trim()}.pdf`;
  if (disposition && /filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i.test(disposition)) {
    const match = disposition.match(/filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i);
    if (match && match[1]) filename = match[1].trim();
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Downloads the INL Term Plan Proposal Form PDF for the given application.
 * Backend fetches journey/lead by application number, fills the template and returns the PDF.
 *
 * @param {Object} params
 * @param {string} params.applicationNumber - Application number (required)
 * @returns {Promise<void>} Resolves when download is triggered; rejects on error
 */
export const downloadProposalForm = async (params,product) => {
  const { applicationNumber } = params || {};
  if (!applicationNumber || String(applicationNumber).trim() === '') {
    throw new Error('Application number is required for proposal form download.');
  }
  const dateOfBirth = resolveOwnershipDob(applicationNumber);
  const search = new URLSearchParams({
    applicationNumber: encryptJourneyQueryValue(applicationNumber),
    ...(dateOfBirth ? { dateOfBirth: encryptJourneyQueryValue(dateOfBirth) } : {}),
    planName: product
  }).toString();
  const response = await api.get(`${API_ENDPOINTS.PROPOSAL_FORM}?${search}`, {
    responseType: 'blob',
  });
  const blob = response?.data;
  if (!blob || !(blob instanceof Blob)) {
    throw new Error('Invalid proposal form response.');
  }
  const disposition = response.headers?.['content-disposition'];
  let filename = `ProposalForm_${String(applicationNumber).trim()}.pdf`;
  if (disposition && /filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i.test(disposition)) {
    const match = disposition.match(/filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i);
    if (match && match[1]) filename = match[1].trim();
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Fetches the filled Nishchit Transfer of Funds consent PDF as a Blob.
 * @param {Object} params
 * @param {string} params.applicationNumber
 * @param {string} [params.dateOfBirth]
 * @param {string} [params.otp]
 * @param {string} [params.consentDate] - dd/MM/yyyy
 * @param {string} [params.mobileNumber]
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export const fetchTofConsentPdfBlob = async (params) => {
  const { applicationNumber, dateOfBirth, otp, consentDate, mobileNumber } = params || {};
  if (!applicationNumber || String(applicationNumber).trim() === '') {
    throw new Error('Application number is required for Transfer of Funds consent PDF.');
  }
  const resolvedDob = resolveOwnershipDob(applicationNumber, dateOfBirth);
  const search = new URLSearchParams({
    applicationNumber: encryptJourneyQueryValue(applicationNumber),
    ...(resolvedDob ? { dateOfBirth: encryptJourneyQueryValue(resolvedDob) } : {}),
    ...(otp ? { otp: String(otp) } : {}),
    ...(consentDate ? { consentDate: String(consentDate) } : {}),
    ...(mobileNumber ? { mobileNumber: String(mobileNumber) } : {}),
  }).toString();
  const response = await api.get(`${API_ENDPOINTS.TOF_CONSENT_PDF}?${search}`, {
    responseType: 'blob',
  });
  const blob = response?.data;
  if (!blob || !(blob instanceof Blob)) {
    throw new Error('Invalid Transfer of Funds consent PDF response.');
  }
  const disposition = response.headers?.['content-disposition'];
  let filename = `FundTransferLetter_${String(applicationNumber).trim()}.pdf`;
  if (disposition && /filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i.test(disposition)) {
    const match = disposition.match(/filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i);
    if (match && match[1]) filename = match[1].trim();
  }
  return { blob, filename };
};

/** Downloads the filled Transfer of Funds consent PDF in the browser. */
export const downloadTofConsentForm = async (params) => {
  const { blob, filename } = await fetchTofConsentPdfBlob(params);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default {
  initiatePaymentFlow,
  generatePaymentUrl,
  initiatePayment,
  buildPaymentRequest,
  getPaymentReturnContext,
  downloadReceipt,
  downloadProposalForm,
  fetchTofConsentPdfBlob,
  downloadTofConsentForm,
};
